import { useEffect, useRef } from 'react';
import { useTenantContext } from '@trashlab/sitesapp/src/contexts/use-tenant-context';
import TagManager from 'react-gtm-module';

// Declared by the SSR handler when GTM is injected server-side into the HTML.
// When this flag is true, the client-side GTM initialization is skipped to
// avoid loading the container twice and double-counting events.
declare global {
  interface Window {
    __GTM_SSR_LOADED__?: boolean;
    dataLayer?: unknown[];
  }
}

export const GoogleAnalytics = () => {
  const { website, isPreview } = useTenantContext();
  const ga4Loaded = useRef(false);
  const gtmInitialized = useRef(false);

  const analyticsEnabled = !isPreview && !!website?.enableGoogleAnalytics;

  const googleAnalyticsId = website?.googleAnalyticsId;

  const googleTagManagerId = website?.googleTagManagerId;

  useEffect(() => {
    if (!analyticsEnabled || !googleTagManagerId || gtmInitialized.current) return;

    // Skip client-side GTM init if the SSR handler already injected GTM.
    if (window.__GTM_SSR_LOADED__) {
      gtmInitialized.current = true;
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        websiteId: website?.id,
        environment: process.env.NODE_ENV,
      });
      return;
    }

    // Fallback: initialize GTM client-side (e.g., dev without SSR)
    TagManager.initialize({
      gtmId: googleTagManagerId,
      dataLayer: {
        websiteId: website?.id,
        environment: process.env.NODE_ENV,
      },
    });

    gtmInitialized.current = true;
  }, [analyticsEnabled, googleTagManagerId, website?.id]);

  // Load Google Analytics (GA4) - only if not using GTM
  useEffect(() => {
    if (!analyticsEnabled || !googleAnalyticsId || googleTagManagerId || ga4Loaded.current) return;

    // Add GA4 script
    const scriptGA = document.createElement('script');
    scriptGA.src = `https://www.googletagmanager.com/gtag/js?id=${googleAnalyticsId}`;
    scriptGA.async = true;
    document.head.appendChild(scriptGA);

    // Add GA4 initialization
    const scriptInit = document.createElement('script');
    scriptInit.innerHTML = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', '${googleAnalyticsId}');
    `;
    document.head.appendChild(scriptInit);

    ga4Loaded.current = true;

    // Cleanup on unmount
    return () => {
      document.head.removeChild(scriptGA);
      document.head.removeChild(scriptInit);
      ga4Loaded.current = false;
    };
  }, [analyticsEnabled, googleAnalyticsId, googleTagManagerId]);

  return null;
}; 
